import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type {
  DigitalItem,
  PhysicalItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Icons } from '@components/Icons/Icons';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useUpdateItem } from '@hooks/cart/useUpdateItem';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { type ReactElement, useState } from 'react';

function isDigitalItem(item: DigitalItem | PhysicalItem): item is DigitalItem {
  return (item as DigitalItem).sku?.toLowerCase().includes('ebook') ?? false;
}

export function Quantity({
  lineItem,
  quantity,
}: {
  lineItem: PhysicalItem | DigitalItem;
  quantity: number;
}): ReactElement {
  const updateItem = useUpdateItem({
    lineItem,
    include: ['line_items.physical_items.options'],
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(false);
  const [errorContent, setErrorContent] = useState(<></>);

  const increaseQuantity = async (n = 1) => {
    const val = Number(quantity) + n;

    if (Number.isInteger(val) && val >= 0) {
      const res = await fetch(
        `/api/product/get-max-quantity?id=${lineItem.product_id}`
      ).then((res) => res.json());

      if (isErrorResponse(res)) {
        setError(true);
        setErrorContent(
          <Paragraph>
            We had a problem updating the quantity:{' '}
            {res.error.message.join('. ')}
          </Paragraph>
        );
        return;
      }
      if (val <= res.data) {
        setUpdating(true);
        setError(false);
        const updated = await updateItem({ quantity: val });
        setUpdating(false);
        if (updated.error) {
          setError(true);
          setErrorContent(
            <Paragraph>
              We had a problem updating the quantity: {updated.error}
            </Paragraph>
          );
        }
      } else {
        setError(true);
        setErrorContent(<Paragraph>Item is at maximum quantity</Paragraph>);
      }
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorContent}
      </ErrorAlert>
      <div className="border border-grey-40 p-1 rounded-full">
        <div className="grid grid-cols-[30px_20px_30px] gap-0">
          {!isDigitalItem(lineItem) && (
            <button
              type="button"
              onClick={() => {
                if (quantity > 1) increaseQuantity(-1);
              }}
              disabled={updating || quantity <= 1}
              data-allowed={quantity > 1}
              className="gae-cart-dec border-none bg-transparent px-0 py-0.5 font-bold uppercase 
              text-grey-dark data-[allowed=false]:cursor-not-allowed 
              data-[allowed=true]:cursor-pointer dark:text-white disabled:cursor-not-allowed"
              aria-label="Decrease quantity by 1"
            >
              <Icons.Minus />
            </button>
          )}
          <VisuallyHidden.Root asChild>
            <label htmlFor="quantity">Quantity</label>
          </VisuallyHidden.Root>
          <input
            type="tel"
            value={quantity}
            id="quantity"
            className="border-none bg-transparent text-center text-black dark:text-white"
            disabled
          />
          {!isDigitalItem(lineItem) && (
            <button
              type="button"
              onClick={() => increaseQuantity(1)}
              disabled={updating}
              className="gae-cart-inc cursor-pointer border-none  bg-transparent px-0 py-0.5 font-bold uppercase 
              text-grey-dark dark:text-white disabled:cursor-not-allowed"
              aria-label="Increase quantity by 1"
            >
              <Icons.Plus />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
